import type { Node } from 'react'
import React from 'react'
import styled from 'styled-components'
import { media, style } from 'app/assets/styles'
import { Link } from 'app/common/link'
import { fonts, H4, SmallText, styleText } from 'app/common/typography'
import { Picture } from 'react-responsive-picture'
import { toSrcSet } from 'shared/utils'
import type { EventMetadata } from 'shared/store/metadata'
import { colors } from 'shared/assets/styles'

const { flameRegular, roboto } = fonts
const { shadow } = colors

type Props = {
    customStyle?: string,
    background?: string,
    btnBackground?: string,
    textColor?: string,
    btnTextColor?: string,
    btnBorder?: string,
    title?: string,
    baseline?: string,
    backgroundImage?: string,
    backgroundImagePortrait?: string,
    image?: Array<string>,
    button?: string,
    actionUrl?: string,
    params?: { [string]: any },
    handleClick?: () => void,
    ratio?: number,
    children?: Node,
    external?: boolean,
    meta?: EventMetadata,
    className?: string
}

const Push = (props: Props) => {
    const {
        background,
        btnBackground,
        textColor,
        title,
        btnTextColor,
        btnBorder,
        baseline,
        backgroundImage,
        backgroundImagePortrait,
        image,
        button,
        actionUrl,
        params,
        handleClick,
        children,
        customStyle,
        ratio,
        external,
        meta,
        className
    } = props

    return (
        <PushWrapper
            className={className}
            backgroundImage={backgroundImage}
            backgroundImagePortrait={backgroundImagePortrait}
            ratio={ratio}
            customStyle={customStyle}>
            <PushContainer className={className} background={background}>
                {children}
                {title && (
                    <StyledH4 className={className} color={textColor}>
                        {title}
                    </StyledH4>
                )}
                {baseline && (
                    <p>
                        <SmallText color={textColor} font={roboto}>
                            {baseline}
                        </SmallText>
                    </p>
                )}
                {image && (
                    <Picture
                        className={className}
                        style={{
                            width: '60%',
                            margin: '20px'
                        }}
                        sources={[
                            {
                                srcSet: toSrcSet(image)
                            }
                        ]}
                    />
                )}
                {button && (
                    <StyledLink
                        className={className}
                        background={btnBackground}
                        border={btnBorder}
                        color={btnTextColor}
                        external={external}
                        url={actionUrl}
                        params={params}
                        meta={meta}
                        onClick={handleClick}>
                        {button}
                    </StyledLink>
                )}
            </PushContainer>
        </PushWrapper>
    )
}

const PushWrapper = styled.div`
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    ${props =>
        props.backgroundImagePortrait &&
        `background-image: url(${props.backgroundImagePortrait})`};
    text-align: center;
    position: relative;
    width: 100%;
    height: auto;
    ${media.medium`
        ${props => (props.customStyle ? props.customStyle : '')};
        width: calc(100% / ${props => (props.ratio ? props.ratio : '2')});
        ${props =>
            props.backgroundImage &&
            `background-image: url(${props.backgroundImage})`};
            
        &.coupon {
            width: calc(50% - 15px);
        }
        
        &.challenge,
        &.privileges {
            width: 100%;
        }
        
        &.dashboard {
            width: calc(100% - 20px);
        }
    `};

    &.coupon,
    &.privileges {
        margin-top: 15px;
        margin-bottom: 15px;
    }

    &.challenge {
        margin-top: 70px;
    }

    &.homeKingdom {
        width: 100%;
        ${media.small`
            ${props =>
                props.backgroundImage &&
                `background-image: url(${props.backgroundImage})`};
        `};
    }

    &.connect {
        box-shadow: 0 0 15px ${shadow};
        width: 90%;
        border-radius: 8px;
        ${media.medium`
            width: calc(100% / ${props => (props.ratio ? props.ratio : '2')});
        `};
    }
`

const PushContainer = styled.div`
    ${props => props.background && `background: ${props.background}`};
    max-width: 500px;
    margin: auto;
    padding: 40px 20px;

    &.homeKingdom {
        max-width: unset;
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        img {
            max-width: 100%;
        }
        ${media.small`
            margin: auto 20px;
            padding: 20px 20px 0;
        `};
    }

    &.coupon,
    &.privileges {
        max-width: unset;
        border-radius: 8px;
        box-shadow: 0 0 20px 0 ${shadow};
        padding: 20px;
    }

    &.jobs {
        padding-top: 70px;
        padding-bottom: 70px;
    }

    &.connect {
        padding: 0 10px 20px;
        border-radius: 8px;
        ${media.medium`
            padding : 20px 0px 40px;
        `};
    }
`

const StyledLink = styled(Link)`
    ${style.buttonMedium};
    border: 0;
    ${props => props.border && `border: 1px solid ${props.border}`};
    &.jobs {
        margin-top: 20px;
        margin-bottom: 20px;
    }

    &.homeKingdom {
        margin-top: 20px;
        ${media.small`
            display: none;
        `}
    }
`

const StyledH4 = styled(H4)`
    margin-top: 20px;

    &.coupon,
    &.privileges {
        ${styleText};
        margin-top: 0;
        font-family: ${flameRegular};

        ${media.medium`
            font-family: ${flameRegular};
        `};
    }
`

export { Push }
